import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router';

import {
  updateBrandColors,
  transformToCodexPayload,
} from '../../helpers/utils';
import OrderView from './OrderView';
import OrderPayment from './OrderPayment';
import AddNewAddress from './AddNewAddress';
import { Currency } from '../../constants/Currency';
import { getOrderDetails } from '../../services/api';
import PageNotFound from '../../router/PageNotFound';
import LocalStorage from '../../helpers/localStorage';
import PaymentStatus, { StatusUI } from './PaymentStatus';
import { OrderStatus } from '../../constants/OrderStatus';
import type { OrderlineOrder } from '../../types/orderlineOrder';
import OrderTypeModal from './Order/OrderTypeModal';

function Checkout() {
  const params = useParams();
  const location = useLocation();
  const orderId = params.orderId;
  const hasRunOnce = useRef(false);
  const [newAddressId, setNewAddressId] = useState<string | null>(null);
  const [startRefetch, setStartRefetch] = useState(false);
  const [typeModalOpen, showTypeModalOpen] = useState(false);
  const isStatusPage = location?.pathname === `/${orderId ?? ''}/status`;

  const {
    error,
    data: orderResponse,
    isLoading: orderLoading,
  } = useQuery({
    retry: false,
    enabled: !!orderId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: () => {
      return isStatusPage && startRefetch ? 2.5 * 1000 : false;
    },
    queryKey: ['getOrderDetails', orderId],
    queryFn: () => {
      if (!orderId) {
        return Promise.reject(new Error('orderId is required.'));
      }
      return getOrderDetails(orderId);
    },
  });

  function isOrderlineOrder(order: unknown): order is OrderlineOrder {
    if (!order || typeof order !== 'object') return false;
    const maybeOrder = order as Partial<OrderlineOrder>;
    return (
      typeof maybeOrder.data === 'object' &&
      typeof maybeOrder.data?.order === 'object' &&
      maybeOrder?.data?.source === 'order_line'
    );
  }

  function onSaveNewAddress(newId: string) {
    setNewAddressId(newId);
    showTypeModalOpen(true);
  }

  const transformedOrder = useMemo(() => {
    return isOrderlineOrder(orderResponse)
      ? transformToCodexPayload(orderResponse)
      : orderResponse;
  }, [orderResponse]);
  const payment = transformedOrder?.payment;
  const settings = transformedOrder?.settings;
  const order = transformedOrder?.order?.order;
  const awaitingPayment =
    order?.details?.order_state === OrderStatus.AWAITING_PAYMENT;
  const awaitingCheckout =
    order?.details?.order_state === OrderStatus.AWAITING_CHECKOUT;

  useEffect(() => {
    if (settings && !hasRunOnce.current) {
      hasRunOnce.current = true;
      if (settings?.currency) {
        LocalStorage.setCompanyCurrency(
          Currency[settings?.currency as keyof typeof Currency] ?? '',
        );
      }
      if (settings?.brand_primary_color && settings?.brand_secondary_color) {
        updateBrandColors(
          settings?.brand_primary_color,
          settings?.brand_secondary_color,
        );
      }
    }
  }, [settings]);

  if (isStatusPage && orderLoading) {
    return <StatusUI status="loading" />;
  }

  if (!orderId || (!orderLoading && !order)) {
    return (
      <PageNotFound
        errorMessage={
          error?.message ?? 'Something went wrong. Please try again later.'
        }
      />
    );
  }

  if (isStatusPage && order) {
    return (
      <PaymentStatus
        order={order}
        orderId={orderId}
        payment={payment}
        setStartRefetch={setStartRefetch}
      />
    );
  }

  if (location?.pathname === `/${orderId}/method`) {
    return (
      <OrderPayment
        orderId={orderId}
        payment={payment}
        loading={orderLoading}
        awaitingPayment={awaitingPayment}
        orderTotal={order?.details?.payable_amount}
      />
    );
  }

  if (location?.pathname === `/${orderId}/new-address`) {
    return (
      <AddNewAddress
        order={order}
        orderId={orderId}
        onSaveNewAddress={onSaveNewAddress}
        customer={transformedOrder?.order?.customer}
      />
    );
  }

  if (location?.pathname === `/${orderId}`) {
    return (
      <>
        <OrderView
          order={order}
          orderId={orderId}
          settings={settings}
          orderLoading={orderLoading}
          awaitingPayment={awaitingPayment}
          awaitingCheckout={awaitingCheckout}
          customer={transformedOrder?.order?.customer}
          updateTypeModalOpen={v => {
            showTypeModalOpen(v);
          }}
        />
        <OrderTypeModal
          order={order}
          isOpen={typeModalOpen}
          newAddressId={newAddressId}
          awaitingCheckout={awaitingCheckout}
          customer={transformedOrder?.order?.customer}
          onClose={() => {
            setNewAddressId(null);
            showTypeModalOpen(false);
          }}
        />
      </>
    );
  }

  return null;
}

export default Checkout;
